import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import axios from 'axios';
import { formatLocalDate } from '../../../utils/dateUtils';

/**
 * Fetch filter options for the Alert Management page
 * Returns hierarchical structure like ExperimentManagement for cascading filters
 */
export const fetchAlertFilterOptions = async (customerId) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(
      `${API_BASE_URL}/alert-management-filters?clientId=${customerId}`,
      { 
        headers: {
          ...createAuthHeader(token),
        }
      }
    );

    // Return the hierarchical structure and alert types for filters
    return { 
      groups: response.data.groups,
      alertTypes: response.data.alertTypes || []
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch alerts based on filters for Kanban view
 * Calls the alerts-management endpoint that returns data organized by status
 */
export const fetchAlerts = async (filters, customerId) => {
  try {
    const token = localStorage.getItem('access_token');
    
    // Build query parameters (same pattern as experiments)
    const params = {
      clientId: customerId,
      startDate: formatDateForAPI(filters.dateRange.start),
      endDate: formatDateForAPI(filters.dateRange.end)
    };
    
    // Add optional filters if they exist
    if (filters.group) params.group = filters.group;
    if (filters.location) params.location = filters.location;
    if (filters.machine) params.machine = filters.machine;
    if (filters.alertType) params.alertType = filters.alertType;
    if (filters.route) params.route = filters.route;
    
    // Build query string
    const queryString = new URLSearchParams(params).toString();
    const apiUrl = `${API_BASE_URL}/alerts-management?${queryString}`;
    
    
    const response = await axios.get(apiUrl, { headers: createAuthHeader(token) });
    
    
    // Response should have alerts organized by status
    // { pending: [...], under_review: [...], resolved: [...] }
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Format date for API (YYYY-MM-DD format)
 */
const formatDateForAPI = (date) => {
  if (!date) return null;
  return formatLocalDate(date);
};

/**
 * Update alert status/action (supports both single and bulk operations)
 * Reuses the existing /api/alerts/update endpoint
 */
export const updateAlertStatus = async (alertIds, action, customerId, metadata = {}) => {
  try {
    const token = localStorage.getItem('access_token');
    
    // Ensure alertIds is always an array
    const idsArray = Array.isArray(alertIds) ? alertIds : [alertIds];
    
    // Ensure all IDs are integers
    const numIds = idsArray.map(id => {
      const numId = parseInt(id, 10);
      if (isNaN(numId)) {
        throw new Error(`Invalid alert ID: ${id}`);
      }
      return numId;
    });
    
    const requestPayload = {
      alertIds: numIds, // API expects array
      action,
      metadata
    };
    
    
    const response = await axios.post(
      `${API_BASE_URL}/alerts/update?clientId=${customerId}`,
      requestPayload,
      { headers: createAuthHeader(token) }
    );
    
    
    return {
      success: true,
      message: numIds.length === 1 
        ? `Alert ${action} successful` 
        : `${numIds.length} alerts ${action} successful`,
      alertIds: numIds,
      data: response.data
    };
  } catch (error) {
    return { 
      success: false, 
      message: `Failed to ${action} alert${Array.isArray(alertIds) && alertIds.length > 1 ? 's' : ''}`,
      error: error.message 
    };
  }
};

/**
 * Mark alert as broken with planned resolution date
 */
export const markAlertAsBroken = async (alertIds, plannedDate, customerId) => {
  
  const result = await updateAlertStatus(
    alertIds, 
    'confirm_broken', 
    customerId,
    { planned_resolution_date: plannedDate }
  );
  
  
  return result;
};

/**
 * Mark alert as resolved
 */
export const markAlertAsResolved = async (alertIds, resolutionDate, customerId) => {
  
  const result = await updateAlertStatus(
    alertIds, 
    'set_resolution_date', 
    customerId,
    { resolution_date: resolutionDate }
  );
  
  
  return result;
};

/**
 * Mark alert as not broken (false positive)
 */
export const markAlertAsNotBroken = async (alertIds, customerId) => {
  
  const result = await updateAlertStatus(alertIds, 'confirm_not_broken', customerId);
  
  
  return result;
};
